if (!customElements.get('cross-page-promos-carousel')) {
  /*
    Carrossel de promos (só no mobile). Revanche:
    - tem começo e fim (sem loop): as setas somem quando não há item daquele lado;
    - arrastar com dedo ou mouse (pointer events), acompanhando o movimento;
    - rotação automática a cada 5s que para no último item.
  */
  class CrossPagePromosCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.cross-page-promos__viewport');
      this.track = this.querySelector('.cross-page-promos__track');
      if (!this.viewport || !this.track || this.closest('.cross-page-promos--hidden')) return;

      this.originalSlides = [...this.track.querySelectorAll(':scope > .cross-page-promos__slide')];
      this.prevButton = this.querySelector('.cross-page-promos__arrow--prev');
      this.nextButton = this.querySelector('.cross-page-promos__arrow--next');
      this.mobileQuery = window.matchMedia('(max-width: 767.98px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleMediaChange = this.syncMode.bind(this);
      this.handleResize = this.updatePosition.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
      this.handlePointerDown = this.onPointerDown.bind(this);
      this.handlePointerMove = this.onPointerMove.bind(this);
      this.handlePointerUp = this.onPointerUp.bind(this);
      this.handleDragStart = (event) => event.preventDefault();
      this.handleClick = this.onClick.bind(this);
      this.handlePrev = () => { this.showPrevious(); this.restartAutoplay(); };
      this.handleNext = () => { this.showNext(); this.restartAutoplay(); };
      this.handlePause = this.pauseAutoplay.bind(this);
      this.handleResume = this.resumeAutoplay.bind(this);

      this.mobileQuery.addEventListener('change', this.handleMediaChange);
      this.reducedMotionQuery.addEventListener('change', this.handleMediaChange);
      this.viewport.addEventListener('keydown', this.handleKeydown);
      this.viewport.addEventListener('pointerdown', this.handlePointerDown);
      this.viewport.addEventListener('pointermove', this.handlePointerMove);
      this.viewport.addEventListener('pointerup', this.handlePointerUp);
      this.viewport.addEventListener('pointercancel', this.handlePointerUp);
      this.viewport.addEventListener('dragstart', this.handleDragStart);
      this.viewport.addEventListener('click', this.handleClick, true);
      this.prevButton?.addEventListener('click', this.handlePrev);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.addEventListener('mouseenter', this.handlePause);
      this.addEventListener('mouseleave', this.handleResume);
      this.addEventListener('focusin', this.handlePause);
      this.addEventListener('focusout', this.handleResume);
      document.addEventListener('visibilitychange', this.handleMediaChange);
      window.addEventListener('resize', this.handleResize);

      this.syncMode();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.mobileQuery?.removeEventListener('change', this.handleMediaChange);
      this.reducedMotionQuery?.removeEventListener('change', this.handleMediaChange);
      this.viewport?.removeEventListener('keydown', this.handleKeydown);
      this.viewport?.removeEventListener('pointerdown', this.handlePointerDown);
      this.viewport?.removeEventListener('pointermove', this.handlePointerMove);
      this.viewport?.removeEventListener('pointerup', this.handlePointerUp);
      this.viewport?.removeEventListener('pointercancel', this.handlePointerUp);
      this.viewport?.removeEventListener('dragstart', this.handleDragStart);
      this.viewport?.removeEventListener('click', this.handleClick, true);
      this.prevButton?.removeEventListener('click', this.handlePrev);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.removeEventListener('mouseenter', this.handlePause);
      this.removeEventListener('mouseleave', this.handleResume);
      this.removeEventListener('focusin', this.handlePause);
      this.removeEventListener('focusout', this.handleResume);
      document.removeEventListener('visibilitychange', this.handleMediaChange);
      window.removeEventListener('resize', this.handleResize);
      this.originalSlides?.forEach((slide) => this.setSlideAvailability(slide, true));
      this.classList.remove('is-mobile-carousel');
      this.track?.style.removeProperty('transform');
    }

    get lastIndex() {
      return this.originalSlides.length - 1;
    }

    syncMode() {
      const shouldEnable = this.mobileQuery.matches && this.originalSlides.length > 1;
      if (shouldEnable && !this.mobileEnabled) {
        this.enableMobileMode();
      } else if (!shouldEnable && this.mobileEnabled) {
        this.disableMobileMode();
      }
      if (this.mobileEnabled) {
        this.updatePosition();
        this.startAutoplay();
      }
    }

    enableMobileMode() {
      this.mobileEnabled = true;
      this.classList.add('is-mobile-carousel');
      this.currentIndex = 0;
      this.updateSlideAvailability();
      this.updatePosition();
      this.startAutoplay();
    }

    disableMobileMode() {
      this.mobileEnabled = false;
      this.stopAutoplay();
      this.classList.remove('is-mobile-carousel');
      this.currentIndex = 0;
      this.track.style.removeProperty('transform');
      this.originalSlides.forEach((slide) => this.setSlideAvailability(slide, true));
      this.updateArrows();
    }

    setSlideAvailability(slide, available) {
      slide.inert = !available;
      if (available) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    updateSlideAvailability() {
      this.originalSlides.forEach((slide, index) => this.setSlideAvailability(slide, index === this.currentIndex));
      this.updateArrows();
    }

    updateArrows() {
      const enabled = Boolean(this.mobileEnabled);
      if (this.prevButton) this.prevButton.hidden = !enabled || this.currentIndex <= 0;
      if (this.nextButton) this.nextButton.hidden = !enabled || this.currentIndex >= this.lastIndex;
    }

    slideWidth() {
      return this.originalSlides[0].getBoundingClientRect().width;
    }

    updatePosition(dragOffset = 0) {
      if (!this.mobileEnabled) return;
      this.track.style.transform = `translate3d(${-this.currentIndex * this.slideWidth() + dragOffset}px, 0, 0)`;
    }

    goTo(index) {
      if (!this.mobileEnabled) return;
      this.currentIndex = Math.max(0, Math.min(this.lastIndex, index));
      this.updateSlideAvailability();
      this.updatePosition();
    }

    showNext() {
      this.goTo(this.currentIndex + 1);
    }

    showPrevious() {
      this.goTo(this.currentIndex - 1);
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showNext();
        this.restartAutoplay();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showPrevious();
        this.restartAutoplay();
      }
    }

    /* ---------- arrastar (dedo ou mouse) ---------- */
    onPointerDown(event) {
      if (!this.mobileEnabled || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.pointer = { id: event.pointerId, startX: event.clientX, startY: event.clientY, dx: 0, dragging: false };
    }

    onPointerMove(event) {
      const p = this.pointer;
      if (!p || event.pointerId !== p.id) return;
      const dx = event.clientX - p.startX;
      const dy = event.clientY - p.startY;
      if (!p.dragging) {
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy)) return;
        p.dragging = true;
        this.stopAutoplay();
        this.classList.add('is-dragging');
        this.track.style.transition = 'none';
        this.viewport.setPointerCapture?.(event.pointerId);
      }
      /* resistência quando não há item daquele lado */
      const atStart = this.currentIndex === 0 && dx > 0;
      const atEnd = this.currentIndex === this.lastIndex && dx < 0;
      p.dx = atStart || atEnd ? dx * 0.3 : dx;
      this.updatePosition(p.dx);
    }

    onPointerUp(event) {
      const p = this.pointer;
      if (!p || event.pointerId !== p.id) return;
      this.pointer = null;
      if (!p.dragging) return;
      this.classList.remove('is-dragging');
      this.track.style.removeProperty('transition');
      this.viewport.releasePointerCapture?.(event.pointerId);
      this.suppressClickUntil = Date.now() + 400;
      const threshold = Math.min(60, this.slideWidth() * 0.15);
      if (event.type !== 'pointercancel' && p.dx <= -threshold && this.currentIndex < this.lastIndex) {
        this.showNext();
      } else if (event.type !== 'pointercancel' && p.dx >= threshold && this.currentIndex > 0) {
        this.showPrevious();
      } else {
        this.updatePosition();
      }
      this.restartAutoplay();
    }

    /* evita abrir o link do item ao soltar depois de arrastar */
    onClick(event) {
      if (!this.suppressClickUntil || Date.now() > this.suppressClickUntil) return;
      this.suppressClickUntil = 0;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    /* ---------- rotação automática: avança a cada 5s e para no último ---------- */
    pauseAutoplay() {
      this.stopAutoplay();
    }

    resumeAutoplay(event) {
      if (event.type === 'focusout' && this.contains(event.relatedTarget)) return;
      this.startAutoplay();
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.mobileEnabled || this.currentIndex >= this.lastIndex || this.reducedMotionQuery.matches || document.hidden || this.matches(':hover') || this.contains(document.activeElement)) return;
      this.autoplayTimer = window.setInterval(() => {
        this.showNext();
        if (this.currentIndex >= this.lastIndex) this.stopAutoplay();
      }, 5000);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  customElements.define('cross-page-promos-carousel', CrossPagePromosCarousel);
}
